import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { useFamilyForm } from '../hooks/useFamilyForm';
import CustomDatePicker from '@shared/components/CustomDatePicker';

export default function FamilyForm({ person }) {
    const { t } = useTranslation();
    const {
        register,
        idnRegister,
        errors,
        handleSubmit,
        onSubmit,
        watch,
        setValue,
        clearErrors,
        isEditing,
        familyMembers,
        handleEdit,
        handleDelete,
        handleCancel,
        hasTaxRelief,
        taxReliefTypes,
        taxReliefPeriods,
        yearOptions
    } = useFamilyForm(person);

    return (
        <div className="family-form-container">
            <h3 className="form-subtitle">
                {t('persons.family.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>
            <div className="form-content">
                <div className="form-section">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.family.name')}<span className="required">*</span></span>
                            <div className='form-input-fields'>
                                <div className="family-form-input-fields">
                                    <input
                                        type="text"
                                        name="firstName"
                                        placeholder={t('persons.physician.firstName')}
                                        className={`small-input ${errors.firstName ? 'is-invalid' : ''}`}
                                        {...register('firstName', {
                                            onChange: (e) => {
                                                const middleName = watch('middleName') || '';
                                                const lastName = watch('lastName') || '';
                                                setValue('name', `${e.target.value} ${middleName} ${lastName}`.trim());
                                            }
                                        })}
                                    />
                                    <input
                                        type="text"
                                        name="middleName"
                                        placeholder={t('persons.physician.middleName')}
                                        className="small-input"
                                        {...register('middleName', {
                                            onChange: (e) => {
                                                const firstName = watch('firstName') || '';
                                                const lastName = watch('lastName') || '';
                                                setValue('name', `${firstName} ${e.target.value} ${lastName}`.trim());
                                            }
                                        })}
                                    />
                                    <input
                                        type="text"
                                        name="lastName"
                                        placeholder={t('persons.physician.lastName')}
                                        className="small-input"
                                        {...register('lastName', {
                                            onChange: (e) => {
                                                const firstName = watch('firstName') || '';
                                                const middleName = watch('middleName') || '';
                                                setValue('name', `${firstName} ${middleName} ${e.target.value}`.trim());
                                            }
                                        })}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('name')}
                                    />
                                </div>
                                {errors.firstName && (
                                    <div className="invalid-feedback d-block">
                                        {errors.firstName.message}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="family-form-input-fields">
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.family.idn')}<span className="required">*</span></span>
                                <div className='form-input-fields'>
                                    <input
                                        type="text"
                                        maxLength={10}
                                        className={`small-input ${errors.idn ? 'is-invalid' : ''}`}
                                        {...idnRegister}
                                    />
                                    {errors.idn && <div className="invalid-feedback">{errors.idn.message}</div>}
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.family.birthDate')}<span className="required">*</span></span>
                                <CustomDatePicker
                                    name="birthDate"
                                    value={watch('birthDate')}
                                    onChange={(date) => {
                                        if (setValue) {
                                            setValue('birthDate', date, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true
                                            });
                                            clearErrors('birthDate');
                                        }
                                    }}
                                    errors={errors}
                                    placeholderText={t('common.dateFormat')}
                                    maxDate={new Date()}
                                    isClearable={true}
                                    isSmall={true}
                                />
                            </div>
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.family.citizenship')}</span>
                                <div className="form-input-radio-fields">
                                    <input
                                        type="radio"
                                        value="bulgarian"
                                        {...register('citizenship')}
                                    />
                                    <label>{t('persons.family.bulgarian')}</label>
                                    <input
                                        type="radio"
                                        value="foreign"
                                        {...register('citizenship')}
                                    />
                                    <label>{t('persons.family.foreign')}</label>
                                </div>
                            </div>
                            
                        </div>
                        <div className="family-form-input-fields">
                        <div className="form-input-wrapper width-30">
                        <span className="form-section-title span-bold">{t('persons.family.childInfo')}</span>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    {...register('school')}
                                />
                                <label>{t('persons.family.isStudent')}</label>
                            </div>
                            </div>
                        <div className="form-input-wrapper width-70">
                            <span className="span-bold">{t('persons.family.childOrder')}</span>
                            <div className='form-input-fields'>
                                <input
                                    type="text"
                                    className="small-input"
                                    {...register('childOrder')}
                                />
                            </div>
                        </div>
                        </div>


                        <span className="form-section-title span-bold">{t('persons.family.taxRelief')}</span>
                        <div className="form-input-wrapper">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    {...register('hasTaxRelief')}
                                />
                                <label>{t('persons.family.hasTaxRelief')}</label>
                            </div>
                        </div>

                        {hasTaxRelief && (
                            <>
                                <div className="form-input-wrapper">
                                    <span className="span-bold">{t('persons.family.taxReliefYear')}</span>
                                    <div className='form-input-fields'>
                                        <select
                                            className="small-select"
                                            {...register('taxReliefYear')}
                                        >
                                            <option value="">{t('common.select')}</option>
                                            {yearOptions.map(year => (
                                                <option key={year.value} value={year.value}>
                                                    {year.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-input-wrapper">
                                    <span className="span-bold">{t('persons.family.taxReliefType')}</span>
                                    <div className='form-input-fields'>
                                        <select className="small-select" {...register('taxReliefType')}>
                                            <option value="">{t('common.select')}</option>
                                            {taxReliefTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-input-wrapper">
                                    <span className="span-bold">{t('persons.family.taxReliefPeriod')}</span>
                                    <div className='form-input-fields'>
                                        <select className="small-select" {...register('taxReliefPeriod')}>
                                            <option value="">{t('common.select')}</option>
                                            {taxReliefPeriods.map(period => (
                                                <option key={period.value} value={period.value}>
                                                    {period.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="email-form-controls">
                            <button
                                type="button"
                                className={`form-btn ${isEditing ? 'btn-orange' : 'btn-green'}`}
                                onClick={handleSubmit(onSubmit)}
                            >
                                {isEditing ? t('common.edit') : t('common.add')}
                            </button>
                            {isEditing && (
                                <button
                                    type="button"
                                    className="form-btn btn-gray"
                                    onClick={handleCancel}
                                >
                                    {t('common.cancel')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
                <div className="table-section">
                    <div className="email-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('persons.family.name')}</th>
                                    <th>{t('persons.family.birthDate')}</th>
                                    <th>{t('persons.family.isForeign')}</th>
                                    <th>{t('persons.family.idn')}</th>
                                    <th>{t('persons.family.isChild')}</th>
                                    <th>{t('persons.family.isStudent')}</th>
                                    <th>{t('persons.family.hasTaxRelief')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {familyMembers.map(member => (
                                    <tr key={member.id}>
                                        <td>{member.name}</td>
                                        <td>{member.birthDate}</td>
                                        <td>{member.citizenship === 'foreign' ? 'Да' : 'Не'}</td>
                                        <td>{member.idn}</td>
                                        <td>{member.childOrder ? 'Да' : 'Не'}</td>
                                        <td>{member.school ? 'Да' : 'Не'}</td>
                                        <td>{member.hasTaxRelief ? 'Да' : 'Не'}</td>
                                        <td className="action-buttons">
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className="action-icon edit"
                                                onClick={() => handleEdit(member)}
                                            />
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="action-icon delete"
                                                onClick={() => handleDelete(member.id)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
} 